import React, { useRef, useState } from "react"
import { useComments } from "../../hooks/useComments"
import LinkifyText from "../shared/LinkifyText"
import { RequestError } from "../shared/RequestError"
import { Spinner } from "../shared/Spinner"
import Carousel from "react-gallery-carousel"
import "react-gallery-carousel/dist/index.css"
import UserAvatar from "../shared/Header/UserAvatar"
import styles from "./ConcertMediaScreen.module.css"
import { ChevronLeft, ChevronRight, Fullscreen, FullscreenExit } from "react-bootstrap-icons"
import { SmartDateFormat } from "../../util/dates"
import { Link } from "react-router-dom"
import FreeTextFormat from "../shared/FreeTextFormat"

const ArrowButton: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    background: "rgba(0,0,0,0.45)",
    borderRadius: "50%",
    width: 36,
    height: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }}>
    {children}
  </div>
)

export interface AttachedMediaItem {
  url: string
  filename: string
  content_type_generic: string
  content_type_specific?: string
  poster_url?: string | null
  video_ready?: boolean
}

interface MediaComment {
  id: number
  user_id: number
  user_avatar_url: string
  username: string
  user_path?: string
  created_at: string
  stripped_comment_text: string
  media_urls: string[]
  attached_media?: AttachedMediaItem[]
}

interface MediaCommentsData {
  comments: MediaComment[]
}

interface AttachedSlide {
  type: "attached"
  key: string
  url: string
  filename: string
  contentTypeGeneric?: string
  contentTypeSpecific?: string
  posterUrl?: string | null
  videoReady?: boolean
}

interface CommentSlide {
  type: "comment"
  key: string
  comment: MediaComment
  url: string
  contentTypeGeneric?: string
  contentTypeSpecific?: string
  posterUrl?: string | null
  videoReady?: boolean
}

type Slide = AttachedSlide | CommentSlide

// Videos are transcoded by Mux asynchronously (VideoConverterService kicks
// off the Mux asset, the Mux webhook flips video_ready once it's done) - the
// original blob's own content type never changes, so readiness has to come
// from video_ready, not the file extension. Until then, show a spinner
// instead of a broken/unplayable element.
const SlideMedia: React.FC<{ slide: Slide; style?: React.CSSProperties }> = ({
  slide,
  style,
}) => {
  if (slide.contentTypeGeneric === "video") {
    if (!slide.videoReady) {
      return (
        <div className="w-full h-full flex items-center justify-center">
          <Spinner />
        </div>
      )
    }
    return (
      <video
        style={style}
        poster={slide.posterUrl ?? undefined}
        preload="metadata"
        controls
      >
        <source src={slide.url} type="video/mp4" />
      </video>
    )
  }

  return <img src={slide.url} alt="direct_upload" style={style} />
}

interface ConcertMediaScreenProps {
  postId: number
  attachedMedia?: AttachedMediaItem[]
  headerText?: string
  className?: string
  coverImageUrl?: string | null
  showHeader?: boolean
  coverMode?: boolean
}

const ConcertMediaScreen: React.FC<ConcertMediaScreenProps> = ({
  postId,
  attachedMedia = [],
  headerText = "Pictures and Videos from the show",
  className = "my-4",
  coverImageUrl,
  showHeader = true,
  coverMode = false,
}) => {
  const PER_PAGE = 35
  const [page] = useState(1)
  const [perPage] = useState(PER_PAGE)

  // react-gallery-carousel keeps every slide mounted (translated offscreen,
  // not unmounted), so a video from a slide you swiped away from keeps
  // playing/making sound unless explicitly paused - there's no single
  // "current slide" video ref to grab, so pause whatever's actually playing
  // in the DOM instead.
  const carouselContainerRef = useRef<HTMLDivElement>(null)
  const handleCarouselIndexChange = () => {
    carouselContainerRef.current?.querySelectorAll("video").forEach((video) => {
      if (!video.paused) video.pause()
    })
  }

  const { isFetching, data, error } = useComments(
    postId,
    page,
    perPage,
    "has_media",
  ) as { isFetching: boolean; data: MediaCommentsData | undefined; error: unknown }

  const coverSlide: AttachedSlide | null = coverImageUrl
    ? {
        type: "attached" as const,
        key: "cover-image",
        url: coverImageUrl,
        filename: "cover",
        contentTypeGeneric: "image",
      }
    : null

  const attachedSlides: AttachedSlide[] =
    attachedMedia?.map((media, index) => ({
      type: "attached" as const,
      key: `attached-${media.url}-${index}`,
      url: media.url,
      filename: media.filename,
      contentTypeGeneric: media.content_type_generic,
      contentTypeSpecific: media.content_type_specific,
      posterUrl: media.poster_url,
      videoReady: media.video_ready,
    })) ?? []

  // Direct-upload comments carry attached_media (content type + poster info
  // needed to tell images from videos apart). Dropbox/drive-link comments
  // have no attached_media at all, so they fall back to the plain url list.
  const commentSlides: CommentSlide[] =
    data?.comments?.flatMap((comment) => {
      if (comment.attached_media && comment.attached_media.length > 0) {
        return comment.attached_media.map((media, mediaIndex) => ({
          type: "comment" as const,
          key: `${comment.id}-${mediaIndex}-${media.url}`,
          comment,
          url: media.url,
          contentTypeGeneric: media.content_type_generic,
          contentTypeSpecific: media.content_type_specific,
          posterUrl: media.poster_url,
          videoReady: media.video_ready,
        }))
      }
      return comment.media_urls.map((url, urlIndex) => ({
        type: "comment" as const,
        key: `${comment.id}-${urlIndex}-${url}`,
        comment,
        url,
      }))
    }) ?? []

  const allSlides: Slide[] = [
    ...(coverSlide ? [coverSlide] : []),
    ...attachedSlides,
    ...commentSlides,
  ]
  const hasSlides = allSlides.length > 0

  return (
    <div className={className}>
      {isFetching && allSlides.length === 0 ? (
        <Spinner />
      ) : error ? (
        <RequestError error={error} />
      ) : hasSlides ? (
        <>
          <ul>
            {showHeader && (
              <h4 className="uppercase font-bold text-gray-700 text-xs">
                {headerText}
              </h4>
            )}

            {coverMode ? (
              // 16:9 full-bleed container using padding-bottom trick for reliable percentage heights
              <div style={{ position: "relative", paddingBottom: "56.25%", backgroundColor: "black", overflow: "hidden" }}>
                <div
                  ref={carouselContainerRef}
                  style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
                >
                  <Carousel
                    canAutoPlay={false}
                    hasIndexBoard={false}
                    hasThumbnails={false}
                    hasLeftButton={allSlides.length > 1 ? "centerLeft" : false}
                    hasRightButton={allSlides.length > 1 ? "centerRight" : false}
                    leftIcon={<ArrowButton><ChevronLeft className="text-white" size={22} /></ArrowButton>}
                    rightIcon={<ArrowButton><ChevronRight className="text-white" size={22} /></ArrowButton>}
                    maxIcon={<Fullscreen className="text-white pr-2" size={30} />}
                    minIcon={<FullscreenExit className="text-white pt-2 pr-2" size={30} />}
                    style={{ backgroundColor: "black", height: "100%" }}
                    className="[&>div>span]:!flex [&>div>span]:!items-center [&>div>span]:!justify-center [&>div>span]:!h-full [&>div>span]:!min-h-0 [&>div>span]:!bg-black"
                    onIndexChange={handleCarouselIndexChange}
                  >
                    {allSlides.map((slide) => (
                      <div key={slide.key} className="flex flex-col h-full min-h-0">
                        {slide.url ? (
                          <div className={`${styles.slideContent} min-h-0 flex-1 bg-black`}>
                            <SlideMedia
                              slide={slide}
                              style={{ width: "100%", height: "100%", objectFit: "contain" }}
                            />
                          </div>
                        ) : null}
                        {slide.type === "comment" && slide.comment.stripped_comment_text?.trim() && (
                          <div className="flex flex-row mt-2 mb-2 bg-gray-100 px-2 pb-2 pt-2 rounded-sm flex-shrink-0">
                            <UserAvatar
                              handleClick={() => {}}
                              user={{ id: slide.comment.user_id, avatar_url_small: slide.comment.user_avatar_url }}
                              size="small"
                              inHeader={false}
                            />
                            <div className="pl-2 w-full" style={{ marginTop: "-2px" }}>
                              <div>
                                <span className="text-gray-900 font-bold mr-3">
                                  {slide.comment.user_path ? (
                                    <Link to={slide.comment.user_path}>{slide.comment.username}</Link>
                                  ) : (
                                    slide.comment.username
                                  )}
                                </span>
                                <span className="text-gray-700 text-xs" style={{ verticalAlign: "text-top" }}>
                                  {SmartDateFormat({ dateStr: slide.comment.created_at, includeTime: true })}
                                </span>
                              </div>
                              <div className="text-sm text-gray-800">
                                <FreeTextFormat text={slide.comment.stripped_comment_text} expandable={false} expanded={true} linkify={true} />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </Carousel>
                </div>
              </div>
            ) : (
              <div
                ref={carouselContainerRef}
                className="bg-black rounded overflow-hidden"
                style={{ height: "400px", padding: "10px 0px" }}
              >
              <Carousel
                canAutoPlay={false}
                hasIndexBoard={false}
                hasThumbnails={false}
                hasLeftButton={allSlides.length > 1}
                hasRightButton={allSlides.length > 1}
                leftIcon={<ArrowButton><ChevronLeft className="text-white" size={22} /></ArrowButton>}
                rightIcon={<ArrowButton><ChevronRight className="text-white" size={22} /></ArrowButton>}
                maxIcon={<Fullscreen className="text-white pr-2" size={30} />}
                minIcon={<FullscreenExit className="text-white pt-2 pr-2" size={30} />}
                style={{
                  backgroundColor: "black",
                }}
                className="[&>div>span]:!flex [&>div>span]:!items-center [&>div>span]:!justify-center [&>div>span]:!min-h-[400px] [&>div>span]:!h-[400px] [&>div>span]:!bg-black"
                onIndexChange={handleCarouselIndexChange}
              >
                {allSlides.map((slide) => (
                  <div key={slide.key} className="flex flex-col h-full min-h-0">
                    {slide.url ? (
                      <div
                        className={`${styles.slideContent} min-h-0 flex-1 bg-black`}
                      >
                        <SlideMedia slide={slide} />
                      </div>
                    ) : (
                      <div
                        className={`${styles.slideContent} min-h-0 flex-1 bg-black`}
                      >
                        <div
                          className="max-w-full max-h-full flex items-center justify-center [&>iframe]:max-h-full [&>iframe]:max-w-full"
                          dangerouslySetInnerHTML={{
                            __html: LinkifyText(slide.url, false),
                          }}
                        />
                      </div>
                    )}

                    {slide.type === "comment" && (
                      <div className="flex flex-row mt-2 mb-2 bg-gray-100 px-2 pb-2 pt-2 rounded-sm flex-shrink-0">
                        <UserAvatar
                          handleClick={() => {}}
                          user={{
                            id: slide.comment.user_id,
                            avatar_url_small: slide.comment.user_avatar_url,
                          }}
                          size="small"
                          inHeader={false}
                        />
                        <div
                          className="pl-2 w-full"
                          style={{ marginTop: "-2px" }}
                        >
                          <div>
                            <span className="text-gray-900 font-bold mr-3">
                              {slide.comment.user_path ? (
                                <Link to={slide.comment.user_path}>
                                  {slide.comment.username}
                                </Link>
                              ) : (
                                slide.comment.username
                              )}
                            </span>
                            <span
                              className="text-gray-700 text-xs"
                              style={{ verticalAlign: "text-top" }}
                            >
                              {SmartDateFormat({
                                dateStr: slide.comment.created_at,
                                includeTime: true,
                              })}
                            </span>
                          </div>
                          <div className="text-sm text-gray-800">
                            <FreeTextFormat
                              text={slide.comment.stripped_comment_text}
                              expandable={false}
                              expanded={true}
                              linkify={true}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </Carousel>
            </div>
            )}
          </ul>
        </>
      ) : null}
    </div>
  )
}

export default ConcertMediaScreen
